<template>
  <div class="mod-payment-channel">
    <div class="page-header">
      <span class="page-header__title">支付通道</span>
    </div>

    <div class="page-body">
      <el-alert
        title="非开发人员请谨慎修改支付配置！启用 / 禁用会直接写入支付通道配置"
        type="warning"
        :closable="false"
        show-icon
        class="warn-tip">
      </el-alert>

      <div class="toolbar">
        <el-input
          v-model="dataForm.merchantsName"
          placeholder="请输入商户名称"
          clearable
          class="toolbar__search"
          @keyup.enter.native="handleSearch">
        </el-input>
        <el-select
          v-model="dataForm.isBackup"
          placeholder="是否作为备用"
          clearable
          class="toolbar__select"
          @change="handleSearch">
          <el-option label="可作为备用" :value="1"></el-option>
          <el-option label="不参与切换" :value="2"></el-option>
        </el-select>
        <el-select
          v-model="dataForm.peyType"
          placeholder="支付方式"
          clearable
          class="toolbar__select"
          @change="handleSearch">
          <el-option
            v-for="item in payTypeOptions"
            :key="item.value"
            :label="item.label"
            :value="item.value">
          </el-option>
        </el-select>
        <el-button type="primary" icon="el-icon-search" @click="handleSearch">查询</el-button>
        <div class="toolbar__right">
          <el-button icon="el-icon-download" @click="handleExport">导出</el-button>
          <el-button type="primary" icon="el-icon-plus" @click="addOrUpdate()">新增通道</el-button>
        </div>
      </div>

      <div class="panel">
        <el-table :data="dataList" v-loading="loading" style="width: 100%;">
          <el-table-column label="通道名称" min-width="140">
            <template slot-scope="scope">{{ scope.row.showName || scope.row.callName || '—' }}</template>
          </el-table-column>
          <el-table-column label="支付类型" width="110">
            <template slot-scope="scope">
              <el-tag v-if="payTypeMap[scope.row.peyType]" size="small" :type="scope.row.peyType === 2 ? 'success' : ''">
                {{ payTypeMap[scope.row.peyType] }}
              </el-tag>
              <span v-else>—</span>
            </template>
          </el-table-column>
          <el-table-column prop="merchantsName" label="商户名称" min-width="180" show-overflow-tooltip></el-table-column>
          <el-table-column label="成功率" width="110">
            <template slot-scope="scope">{{ rateLabel(scope.row.successRate) }}</template>
          </el-table-column>
          <el-table-column label="健康分数" width="110">
            <template slot-scope="scope">{{ scope.row.healthScore === null ? '—' : scope.row.healthScore }}</template>
          </el-table-column>
          <el-table-column prop="sort" label="权重" width="90"></el-table-column>
          <el-table-column label="支付金额范围" width="170">
            <template slot-scope="scope">{{ amountRange(scope.row) }}</template>
          </el-table-column>
          <el-table-column label="备用切换" width="120">
            <template slot-scope="scope">
              <el-tag :type="scope.row.isBackup === 1 ? '' : 'info'" size="small">
                {{ scope.row.isBackup === 1 ? '可备用' : '不切换' }}
              </el-tag>
            </template>
          </el-table-column>
          <el-table-column label="状态" width="100">
            <template slot-scope="scope">
              <el-tag :type="isEnabled(scope.row) ? 'success' : 'info'" size="small">
                {{ isEnabled(scope.row) ? '启用' : '关闭' }}
              </el-tag>
            </template>
          </el-table-column>
          <el-table-column label="操作" width="140" fixed="right">
            <template slot-scope="scope">
              <el-button type="text" size="small" @click="addOrUpdate(scope.row.id)">编辑</el-button>
              <el-button
                type="text"
                size="small"
                :class="isEnabled(scope.row) ? 'danger-text' : ''"
                @click="handleToggle(scope.row)">
                {{ isEnabled(scope.row) ? '禁用' : '启用' }}
              </el-button>
            </template>
          </el-table-column>
        </el-table>
        <el-pagination
          :current-page="page"
          :page-sizes="[10, 20, 50]"
          :page-size="limit"
          :total="total"
          layout="total, sizes, prev, pager, next, jumper"
          class="pagination"
          @size-change="handleSizeChange"
          @current-change="handlePageChange">
        </el-pagination>
      </div>
    </div>

    <!-- 弹窗：新增 / 修改通道（复用支付配置模块） -->
    <add-or-update v-if="dialogVisible" ref="addOrUpdate" @refreshDataList="loadList"></add-or-update>
  </div>
</template>

<script>
import AddOrUpdate from '../payConfig/payconfig-add-or-update'
import {
  getPayConfigPage,
  getPayConfigInfo,
  updatePayConfig,
  buildPayConfigExportUrl,
  PAY_TYPE_MAP,
  PAY_CONFIG_TYPE_OPTIONS,
  CONFIG_STATUS
} from '@/api/trade'

export default {
  name: 'PaymentChannel',
  components: {
    AddOrUpdate
  },
  data () {
    return {
      loading: false,
      dataList: [],
      page: 1,
      limit: 10,
      total: 0,
      dataForm: {
        merchantsName: '',
        isBackup: '',
        peyType: ''
      },
      dialogVisible: false
    }
  },

  computed: {
    payTypeMap () {
      return PAY_TYPE_MAP
    },
    payTypeOptions () {
      return PAY_CONFIG_TYPE_OPTIONS
    }
  },

  created () {
    this.loadList()
  },

  methods: {
    buildParams () {
      const params = {
        page: this.page,
        limit: this.limit
      }
      if (this.dataForm.merchantsName) {
        params.merchantsName = this.dataForm.merchantsName
      }
      if (this.dataForm.isBackup !== '') {
        params.isBackup = this.dataForm.isBackup
      }
      // 后端要求 peyType 大于 0 才参与过滤
      if (this.dataForm.peyType !== '') {
        params.peyType = this.dataForm.peyType
      }
      return params
    },

    loadList () {
      this.loading = true
      return getPayConfigPage(this.buildParams()).then(({ data: res }) => {
        if (res.code !== 0) {
          return this.$message.error(res.msg)
        }
        // 接口已按 sort ASC 返回，保持后端顺序
        this.dataList = (res.data && res.data.list) || []
        this.total = (res.data && res.data.total) || 0
      }).catch(() => {
        this.$message.error('加载支付通道失败')
      }).finally(() => {
        this.loading = false
      })
    },

    handleSearch () {
      this.page = 1
      this.loadList()
    },

    handleSizeChange (val) {
      this.limit = val
      this.page = 1
      this.loadList()
    },

    handlePageChange (val) {
      this.page = val
      this.loadList()
    },

    handleExport () {
      const params = this.buildParams()
      delete params.page
      delete params.limit
      window.location.href = buildPayConfigExportUrl(params)
    },

    addOrUpdate (id) {
      this.dialogVisible = true
      this.$nextTick(() => {
        this.$refs.addOrUpdate.dataForm.id = id || ''
        this.$refs.addOrUpdate.init()
      })
    },

    isEnabled (row) {
      return Number(row.status) !== CONFIG_STATUS.DISABLED
    },

    // 先取完整配置再整体回写，避免 PUT 时丢字段
    handleToggle (row) {
      const enabled = this.isEnabled(row)
      const nextStatus = enabled ? CONFIG_STATUS.DISABLED : CONFIG_STATUS.ENABLED
      this.$confirm(`确定${enabled ? '禁用' : '启用'}通道「${row.showName || row.merchantsName}」？`, '提示', {
        type: 'warning'
      }).then(() => {
        return getPayConfigInfo(row.id)
      }).then(({ data: res }) => {
        if (res.code !== 0) {
          return Promise.reject(new Error(res.msg || '获取通道配置失败'))
        }
        return updatePayConfig({ ...res.data, status: nextStatus })
      }).then(({ data: res }) => {
        if (res.code !== 0) {
          return Promise.reject(new Error(res.msg || '操作失败'))
        }
        this.$message.success(enabled ? '已禁用' : '已启用')
        this.loadList()
      }).catch(e => {
        // 取消确认框时 e 为字符串 cancel
        if (e && e.message) {
          this.$message.error(e.message)
        }
      })
    },

    // 订单金额需满足 payMin <= amount <= payMax，两端都含边界
    amountRange (row) {
      const min = row.payMin
      const max = row.payMax
      if (min === null && max === null) {
        return '—'
      }
      const fmt = value => (value === null || value === undefined ? '不限' : `¥${Number(value).toFixed(2)}`)
      return `${fmt(min)} ~ ${fmt(max)}`
    },

    rateLabel (rate) {
      if (rate === null || rate === undefined || rate === '') {
        return '—'
      }
      return `${Number(rate).toFixed(1)}%`
    }
  }
}
</script>

<style lang="scss" scoped>
.mod-payment-channel {
  background-color: #f5f7fa;
  min-height: 100%;
}

.page-header {
  display: flex;
  align-items: center;
  padding: 12px 20px;
  background-color: #fff;
  border-bottom: 1px solid #ebeef5;

  &__title {
    font-size: 16px;
    font-weight: 600;
    color: #1f2d3d;
  }
}

.page-body {
  padding: 16px;
}

.warn-tip {
  margin-bottom: 16px;
}

.toolbar {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  margin-bottom: 16px;

  &__search {
    width: 200px;
    margin-right: 12px;
  }

  &__select {
    width: 160px;
    margin-right: 12px;
  }

  &__right {
    margin-left: auto;
  }
}

.panel {
  background-color: #fff;
  border: 1px solid #ebeef5;
  border-radius: 8px;
  padding: 0 16px 16px;
}

.danger-text {
  color: #f56c6c;
}

.pagination {
  margin-top: 16px;
  text-align: right;
}

::v-deep .el-table th {
  background-color: #fafbfc;
  color: #909399;
  font-weight: 500;
}
</style>
